/**
 @license
 Copyright (c) 2015-2026 Lablup Inc. All rights reserved.
 */
/**
 * WebUI Config - React-based config.toml parsing and state management.
 *
 * This module replaces the Lit shell's _parseConfig() and loadConfig() methods.
 * It fetches config.toml, parses it with smol-toml, preprocesses values,
 * and provides the parsed config to React components via Jotai atoms.
 *
 * Config values are also set on globalThis for backward compatibility with
 * non-React code (e.g. globalThis.packageEdition, globalThis.packageValidUntil).
 */
import {
  getDefaultLoginConfig,
  refreshConfigFromToml,
  type LoginConfigState,
} from '../helper/loginConfig';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect } from 'react';
import { parse as toml } from 'smol-toml';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * The raw parsed TOML config object (before field-level interpretation).
 * This mirrors the shape returned by markty-toml for config.toml.
 */
export interface RawTomlConfig {
  general?: Record<string, unknown>;
  license?: {
    edition?: string;
    validUntil?: string;
    [key: string]: unknown;
  };
  wsproxy?: {
    proxyURL?: string;
    [key: string]: unknown;
  };
  plugin?: {
    login?: string;
    page?: string;
    [key: string]: unknown;
  };
  resources?: Record<string, unknown>;
  environments?: Record<string, unknown>;
  menu?: Record<string, unknown>;
  pipeline?: Record<string, unknown>;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------

/**
 * Holds the raw parsed config.toml object.
 * Set once during app initialization, may be updated for Electron webserver merge.
 */
export const rawConfigState = atom<RawTomlConfig | null>(null);

/**
 * Holds the processed LoginConfigState derived from the raw TOML config.
 */
export const loginConfigState = atom<LoginConfigState | null>(null);

/**
 * Indicates whether config.toml has been fetched and processed.
 */
export const configLoadedState = atom<boolean>(false);

/**
 * Holds the auto_logout flag extracted from config.
 */
export const autoLogoutState = atom<boolean>(false);

/**
 * Holds the proxy URL from config.wsproxy.proxyURL.
 */
export const proxyUrlState = atom<string>('');

/**
 * Holds the login plugin name from config.plugin.login.
 */
export const loginPluginState = atom<string>('');

/**
 * Module-level flag to ensure config.toml is fetched only once,
 * even when multiple component instances call useInitializeConfig.
 */
let configInitialized = false;

// ---------------------------------------------------------------------------
// TOML fetching and preprocessing
// ---------------------------------------------------------------------------

/**
 * Preprocess the TOML config to handle escape sequences in apiEndpointText.
 * This mirrors the Lit shell's _preprocessToml logic.
 */
function preprocessToml(config: RawTomlConfig): void {
  if (config?.general?.apiEndpointText) {
    try {
      config.general.apiEndpointText = JSON.parse(
        `"${config.general.apiEndpointText}"`,
      );
    } catch {
      // If JSON.parse fails, keep the original value
    }
  }
}

/**
 * Fetch and parse config.toml from the given path.
 * Returns the parsed TOML object, or null on failure.
 */
export async function fetchAndParseConfig(
  configPath: string,
): Promise<RawTomlConfig | null> {
  try {
    const res = await fetch(configPath);
    if (res.status !== 200) {
      return null;
    }
    // When config.toml is missing, the dev server's SPA fallback
    // (historyApiFallback) serves index.html with status 200.  Detect this
    // by checking the Content-Type header — a real TOML file is served as
    // text/plain or application/octet-stream, never text/html.
    const contentType = res.headers.get('content-type') ?? '';
    if (contentType.includes('text/html')) {
      return null;
    }
    const text = await res.text();
    const parsed = toml(text) as RawTomlConfig;
    preprocessToml(parsed);
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Process the raw config to extract config values and set global state.
 * This replaces the Lit shell's loadConfig() method.
 */
function processConfig(config: RawTomlConfig): {
  autoLogout: boolean;
  proxyUrl: string;
  loginPlugin: string;
  loginConfig: LoginConfigState;
} {
  // Auto-logout setting
  let autoLogout = false;
  const storedAutoLogout = (globalThis as Record<string, unknown>)
    .backendaioptions
    ? (
        (globalThis as Record<string, unknown>).backendaioptions as {
          get: (key: string, defaultValue?: unknown) => unknown;
        }
      ).get('auto_logout')
    : null;

  if (
    storedAutoLogout === null &&
    config.general !== undefined &&
    'autoLogout' in config.general
  ) {
    autoLogout = config.general.autoLogout as boolean;
  } else {
    autoLogout = (storedAutoLogout as boolean) ?? false;
  }

  // Package edition and license info
  let edition = 'Open Source';
  let validUntil = '';

  if (config.license !== undefined && 'edition' in config.license) {
    edition = config.license.edition ?? 'Open Source';
  }
  (globalThis as Record<string, unknown>).packageEdition = edition;

  if (config.license !== undefined && 'validUntil' in config.license) {
    validUntil = config.license.validUntil ?? '';
  }
  (globalThis as Record<string, unknown>).packageValidUntil = validUntil;

  // Proxy URL
  let proxyUrl = '';
  if (config.wsproxy !== undefined && 'proxyURL' in config.wsproxy) {
    proxyUrl = config.wsproxy.proxyURL ?? '';
  }

  // Login plugin
  let loginPlugin = '';
  if (config.plugin !== undefined && 'login' in config.plugin) {
    loginPlugin = config.plugin.login ?? '';
  }

  // Derive the full LoginConfigState from the raw config
  const loginConfig = refreshConfigFromToml(config);

  return {
    autoLogout,
    proxyUrl,
    loginPlugin,
    loginConfig,
  };
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Hook that initializes the config system by fetching and parsing config.toml.
 * Should be called once at the app root level (e.g., in the Lit shell bridge
 * or in a top-level React component).
 *
 * This replaces the Lit shell's _parseConfig() + loadConfig() flow.
 */
export function useInitializeConfig(): {
  isLoaded: boolean;
  rawConfig: RawTomlConfig | null;
  loginConfig: LoginConfigState | null;
  loadConfig: () => Promise<void>;
} {
  const setRawConfig = useSetAtom(rawConfigState);
  const setLoginConfig = useSetAtom(loginConfigState);
  const setConfigLoaded = useSetAtom(configLoadedState);
  const setAutoLogout = useSetAtom(autoLogoutState);
  const setProxyUrl = useSetAtom(proxyUrlState);
  const setLoginPlugin = useSetAtom(loginPluginState);

  const isLoaded = useAtomValue(configLoadedState);
  const rawConfig = useAtomValue(rawConfigState);
  const currentLoginConfig = useAtomValue(loginConfigState);

  const loadConfig = useCallback(async () => {
    // Use a module-level flag to prevent multiple LoginView instances
    // (eager + lazy) from fetching config.toml more than once.
    // A per-instance useRef is insufficient because each component
    // instance gets its own ref.
    if (configInitialized) return;
    configInitialized = true;

    // Electron uses es6:// protocol which resolves from app/ directory
    // Web uses relative path from the HTML location
    const configPath = (globalThis as Record<string, unknown>).isElectron
      ? 'es6://config.toml'
      : '../../config.toml';

    const parsed = await fetchAndParseConfig(configPath);

    if (!parsed) {
      // config.toml is missing or failed to load — apply defaults so the app
      // remains functional (login page renders with empty API endpoint field).
      const defaultConfig = getDefaultLoginConfig();
      setLoginConfig(defaultConfig);
      setProxyUrl(defaultConfig.proxy_url);
      setAutoLogout(false);
      setConfigLoaded(true);

      // Ensure config-derived globals are set even when config.toml is missing,
      // so edition-dependent logic behaves consistently with the normal path.
      const globalScope = globalThis as Record<string, unknown>;
      if (globalScope.packageEdition === undefined) {
        globalScope.packageEdition = 'Open Source';
      }
      if (globalScope.packageValidUntil === undefined) {
        globalScope.packageValidUntil = '';
      }

      // eslint-disable-next-line no-console
      console.warn('config.toml not found — using default configuration');
      return;
    }

    setRawConfig(parsed);

    const { autoLogout, proxyUrl, loginPlugin, loginConfig } =
      processConfig(parsed);

    setLoginConfig(loginConfig);
    setAutoLogout(autoLogout);
    setProxyUrl(proxyUrl);
    setLoginPlugin(loginPlugin);

    setConfigLoaded(true);
  }, [
    setRawConfig,
    setLoginConfig,
    setConfigLoaded,
    setAutoLogout,
    setProxyUrl,
    setLoginPlugin,
  ]);

  return {
    isLoaded,
    rawConfig,
    loginConfig: currentLoginConfig,
    loadConfig,
  };
}

/**
 * Hook to access the processed login config state.
 */
export function useLoginConfig(): LoginConfigState | null {
  return useAtomValue(loginConfigState);
}

/**
 * Hook to access the raw config.toml state.
 */
export function useRawConfig(): RawTomlConfig | null {
  return useAtomValue(rawConfigState);
}

/**
 * Hook to check if config has been loaded.
 */
export function useConfigLoaded(): boolean {
  return useAtomValue(configLoadedState);
}

/**
 * Hook to access the auto-logout setting.
 */
export function useAutoLogout(): boolean {
  return useAtomValue(autoLogoutState);
}

/**
 * Hook to access the proxy URL.
 */
export function useProxyUrl(): string {
  return useAtomValue(proxyUrlState);
}

/**
 * Hook to update the raw config (e.g., after Electron webserver config merge).
 * Returns a setter that re-processes config after updating.
 */
export function useUpdateRawConfig(): (config: RawTomlConfig) => void {
  const setRawConfig = useSetAtom(rawConfigState);
  const setLoginConfig = useSetAtom(loginConfigState);
  const setAutoLogout = useSetAtom(autoLogoutState);
  const setProxyUrl = useSetAtom(proxyUrlState);
  const setLoginPlugin = useSetAtom(loginPluginState);

  return useCallback(
    (config: RawTomlConfig) => {
      setRawConfig(config);
      const { autoLogout, proxyUrl, loginPlugin, loginConfig } =
        processConfig(config);
      setLoginConfig(loginConfig);
      setAutoLogout(autoLogout);
      setProxyUrl(proxyUrl);
      setLoginPlugin(loginPlugin);
    },
    [setRawConfig, setLoginConfig, setAutoLogout, setProxyUrl, setLoginPlugin],
  );
}

/**
 * Effect hook that sets up the proxy URL on the backend client
 * when the user connects (replaces Lit shell's refreshPage).
 */
export function useConfigRefreshPageEffect(): void {
  const proxyUrl = useAtomValue(proxyUrlState);

  useEffect(() => {
    const handleConnected = () => {
      const client = (globalThis as Record<string, unknown>).backendaiclient as
        | { proxyURL?: string }
        | undefined;
      if (client) {
        client.proxyURL = proxyUrl;
      }
    };

    document.addEventListener('backend-ai-connected', handleConnected);
    return () => {
      document.removeEventListener('backend-ai-connected', handleConnected);
    };
  }, [proxyUrl]);
}
